/**
 * @file useDashboard.js
 *
 * Dashboard Hook
 *
 * Fetches aggregate stats and subscription status for the dashboard page.
 * Both API calls run in parallel via Promise.allSettled so a Stripe failure
 * doesn't block the appointment stats from rendering.
 */
import { useState, useEffect, useCallback } from "react";
import { appointmentService, subscriptionService } from "../services";

/**
 * Hook that fetches dashboard statistics and subscription usage.
 *
 * @returns {{stats: Object|null, subscription: Object|null, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function useDashboard(enabled = true) {
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!enabled) return;
    try {
      setIsLoading(true);
      setError(null);
      const [statsResult, subResult] = await Promise.allSettled([
        appointmentService.getStats(),
        subscriptionService.getStatus(),
      ]);
      if (statsResult.status === "fulfilled") setStats(statsResult.value.data);
      if (subResult.status === "fulfilled") setSubscription(subResult.value.data);
      if (statsResult.status === "rejected") setError(statsResult.reason);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, subscription, isLoading, error, refresh: fetchStats };
}
